<template>
  <v-navigation-drawer
    :value="value"
    right
    temporary
    fixed
    width="420"
    class="invited-drawer"
    @input="$emit('input', $event)"
  >
    <!-- Header -->
    <div class="invited-drawer__header">
      <div class="invited-drawer__title">
        {{ $t("broker.members.membersInvitedTitle") }}
      </div>

      <button icon @click="$emit('input', false)">
        <v-icon>fa-regular fa-xmark</v-icon>
      </button>
    </div>

    <v-divider />

    <!-- Body -->
    <div class="invited-drawer__body">
      <div class="invited-drawer__sectionTitle">
        {{ $t("broker.members.membersInvited") }}
      </div>

      <div
        v-for="group in groupedInvites"
        :key="group.key"
        class="invited-drawer__group"
      >
        <div class="invited-drawer__groupTitle">{{ group.title }}</div>

        <div
          v-for="item in group.items"
          :key="item.id"
          class="invited-drawer__rowItem"
        >
          <div class="invited-drawer__left">
            <v-icon size="16">fa-regular fa-user</v-icon>
            <div class="invited-drawer__email">{{ item.email }}</div>
          </div>

          <div class="invited-drawer__center">
            <div
              class="invited-drawer__roleChip"
              :class="`invited-drawer__roleChip--${item.role?.toLowerCase()}`"
            >
              {{ roleLabel(item.role) }}
            </div>
          </div>
          <div class="invited-drawer__actions">
            <button
              icon
              :disabled="actionLoadingId === item.id"
              @click="onResend(item)"
            >
              <v-icon size="18">fa-regular fa-rotate-left</v-icon>
            </button>

            <button
              icon
              :disabled="actionLoadingId === item.id"
              @click="onDelete(item)"
            >
              <v-icon size="18">fa-regular fa-trash-can</v-icon>
            </button>
          </div>
        </div>

        <v-divider class="my-3" />
      </div>
    </div>

    <v-snackbar
      v-model="snackbar.show"
      :timeout="2500"
      bottom
      right
      class="invited-drawer__snackbar"
    >
      {{ snackbar.text }}

      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false"> OK </v-btn>
      </template>
    </v-snackbar>
  </v-navigation-drawer>
</template>

<script>
export default {
  name: "InvitedMembersDrawer",
  props: {
    value: { type: Boolean, default: false },
    invites: { type: Array, default: () => [] }, // lista de invitaciones
  },
  data() {
    return {
      actionLoadingId: null,
      snackbar: {
        show: false,
        text: "",
      },
    };
  },
  computed: {
    groupedInvites() {
      const now = Date.now();
      const msPerDay = 24 * 60 * 60 * 1000;
      const lastWeek = [];
      const moreThan7 = [];
      const lastMonth = [];

      this.invites.forEach((item) => {
        const sentAt = item.lastSent ? new Date(item.lastSent).getTime() : null;
        if (sentAt == null || Number.isNaN(sentAt)) {
          lastMonth.push(item);
          return;
        }
        const daysAgo = (now - sentAt) / msPerDay;
        if (daysAgo <= 7) lastWeek.push(item);
        else if (daysAgo <= 30) moreThan7.push(item);
        else lastMonth.push(item);
      });

      const sortByLastSentDesc = (a, b) => {
        const tA = a.lastSent ? new Date(a.lastSent).getTime() : 0;
        const tB = b.lastSent ? new Date(b.lastSent).getTime() : 0;
        return tB - tA;
      };
      lastWeek.sort(sortByLastSentDesc);
      moreThan7.sort(sortByLastSentDesc);
      lastMonth.sort(sortByLastSentDesc);

      const groups = [];
      if (lastWeek.length)
        groups.push({
          key: "lw",
          title: this.$t("broker.members.invitedLastWeek"),
          items: lastWeek,
        });
      if (moreThan7.length)
        groups.push({
          key: "m7",
          title: this.$t("broker.members.invitedMore7Days"),
          items: moreThan7,
        });
      if (lastMonth.length)
        groups.push({
          key: "lm",
          title: this.$t("broker.members.invitedLastMonth"),
          items: lastMonth,
        });

      return groups;
    },
  },
  methods: {
    roleLabel(role) {
      return role === "ADMIN" ? "Admin" : "Read-Only";
    },

    async onDelete(item) {
      if (!item?.id) return;
      this.actionLoadingId = item.id;

      try {
        // emitimos al padre y esperamos confirmación via callback
        await new Promise((resolve, reject) => {
          this.$emit("delete", item, { resolve, reject });
        });

        this.showSnackbar(
          this.$t("broker.members.inviteDeleted") || "Invitation deleted",
        );
      } catch (e) {
        this.showSnackbar(
          this.$t("broker.members.actionFailed") || "Action failed",
        );
      } finally {
        this.actionLoadingId = null;
      }
    },

    async onResend(item) {
      if (!item?.id) return;
      this.actionLoadingId = item.id;

      try {
        await new Promise((resolve, reject) => {
          this.$emit("resend", item, { resolve, reject });
        });

        this.showSnackbar(
          this.$t("broker.members.inviteResent") || "Invitation resent",
        );
      } catch (e) {
        this.showSnackbar(
          this.$t("broker.members.actionFailed") || "Action failed",
        );
      } finally {
        this.actionLoadingId = null;
      }
    },
    showSnackbar(text) {
      this.snackbar.text = text;
      this.snackbar.show = true;
    },
  },
};
</script>

<style lang="scss" scoped>
.invited-drawer {
  display: flex;
  flex-direction: column;

  &__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    padding: 18px 18px 12px;
  }

  &__title {
    font-size: 18px;
    font-weight: 800;
    line-height: 1.2;
  }

  &__body {
    padding: 14px 18px;
    flex: 1;
    overflow: auto;
  }

  &__sectionTitle {
    font-size: 12px;
    font-weight: 800;
    margin-bottom: 10px;
    opacity: 0.85;
  }

  &__groupTitle {
    font-size: 11px;
    opacity: 0.55;
    margin: 12px 0 8px;
  }

  &__rowItem {
    display: grid;
    grid-template-columns: 2fr 1fr auto;
    align-items: center;
    gap: 12px;
    padding: 10px 0;
  }

  &__center {
    display: flex;
    justify-content: center; 
    align-items: center;
    min-width: 0;
  }

  &__left {
    display: flex;
    align-items: start;
    gap: 10px;
    min-width: 0;
  }

  &__email {
    color: #3b3b3b !important;
    font-size: 13px;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 190px;
  }

  &__right {
    display: flex;
    width: 145px;
    align-items: center;
    gap: 5px;
  }

  &__roleChip {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 6px 12px;
    border-radius: 999px;
    font-weight: 700;
    width: fit-content;
    font-size: 12px;
    max-width: 100%;
    margin-right: 0;
    white-space: nowrap;
  }

  &__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end; 
  gap: 6px;
}

  &__roleChip--read_only,
  &__roleChip--readonly {
    background: #d7ffed;
    color: #005931 !important;
  }

  &__roleChip--admin {
    background: #e0f3fe;
    color: #045684 !important;
  }
}

.broker-app .v-application.theme--light .invited-drawer__email {
  color: #3b3b3b !important;
}
</style>
